import * as FlipdishApi from '@flipdish/api-client-typescript';
import { createAction } from 'redux-act';

import { languageActions } from '../actions/language.actions';
import { accountConstants } from '../constants/account.constants';
import { CookiesPreferences } from '../helpers/gdpr';
import { timeZoneDefinitions } from '../helpers/timeZones';
import { passwordValidationApiErrors } from '../helpers/validation';
import { notifyError, notifySaved } from '../layouts/Notify/actions';
import { accountService } from '../services/account.service';
import { googleAnalytics } from '../services/google_analytics';
import { closeConnection } from '../services/signalr';
import { action, actionError } from './utils';

function setupSurvey(account) {
  return (dispatch) => {
    dispatch({ type: accountConstants.SETUP_SURVEY, account });
  };
}

function setTimeZoneBasedOnLatLng(latitude: number, longitude: number) {
  return (dispatch) => {
    accountService.getIanaTimeZoneFromGmaps(latitude, longitude).then((timeZoneId) => {
      const updateModel = new FlipdishApi.AccountDetailBase();
      updateModel.TimeZoneInfoId = timeZoneId;
      dispatch(update(updateModel));
    });
  };
}

// #region getSupportedCountries
const { GET_SUPPORTED_COUNTRIES } = accountConstants;

export type GetSupCountries = ReturnType<typeof getSupCountries>;
export const getSupCountries = (props) => action(GET_SUPPORTED_COUNTRIES, props);

function getSupportedCountries(throwError = false) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      const supportedCountries = await accountService.getSupCountries();
      dispatch(getSupCountries(supportedCountries));
    } catch (error) {
      if (throwError) {
        throw error;
      }
    }
  };
}
// #endregion

// #region create
const { CREATE_REQUEST, CREATE_SUCCESS, CREATE_FAILURE } = accountConstants;

export type CreateRequest = ReturnType<typeof createRequest>;
export const createRequest = (props: CreateProps) => action(CREATE_REQUEST, props);

export type CreateSuccess = ReturnType<typeof createSuccess>;
export const createSuccess = (props: CreateProps) => action(CREATE_SUCCESS, props);

export type CreateFailure = ReturnType<typeof createFailure>;
export const createFailure = (props: CreateProps, error: Error) =>
  actionError(CREATE_FAILURE, props, error);

export type CreateProps = {} & {
  email: string;
  storeName: string;
  languageCode: string;
  Rid?: number;
  Cid?: string;
  RecaptchaToken?: string;
};
export function create(props: CreateProps) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(createRequest(props));

      await accountService.create(props);
      dispatch(createSuccess(props));
    } catch (error) {
      dispatch(createFailure(props, error));
      throw error;
    }
  };
}
// #endregion

// #region setRefferer
const { SET_REFERRER } = accountConstants;

export type SetReferrer = ReturnType<typeof setReferrer>;
export const setReferrer = (referralIds) => action(SET_REFERRER, referralIds);
// #endregion

// #region update
const { UPDATE_REQUEST, UPDATE_SUCCESS, UPDATE_FAILURE } = accountConstants;

export type UpdateRequest = ReturnType<typeof updateRequest>;
export const updateRequest = (props: FlipdishApi.AccountDetailBase) =>
  action(UPDATE_REQUEST, props);

export type UpdateSuccess = ReturnType<typeof updateSuccess>;
export const updateSuccess = (props: FlipdishApi.AccountDetailBase) =>
  action(UPDATE_SUCCESS, props);

export type UpdateFailure = ReturnType<typeof updateFailure>;
export const updateFailure = (props: FlipdishApi.AccountDetailBase, error: Error) =>
  actionError(UPDATE_FAILURE, props, error);

function update(props: FlipdishApi.AccountDetailBase) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(updateRequest(props));
      await accountService.updateAccount(props);
      dispatch(updateSuccess(props));
      dispatch(getAccountDetails());
    } catch (error) {
      dispatch(updateFailure(props, error));
      throw error;
    }
  };
}
//#endregion

function updatePassword(passwordChange: FlipdishApi.ChangePasswordModel) {
  return (dispatch) => {
    dispatch(request());
    return accountService.updateAccountPassword(passwordChange).then(
      () => {
        dispatch(success());
        dispatch(getAccountDetails());
      },
      (error) => {
        const message = error.body?.Message;
        dispatch(failure(message));
        if (passwordValidationApiErrors.includes(error.body?.Message)) {
          dispatch(notifyError({ message: error.body?.Message, translate: true }));
        } else {
          dispatch(notifyError({ message: 'Something_went_wrong', translate: true }));
        }
      }
    );
  };

  function request() {
    return { type: accountConstants.UPDATE_PASSWORD_REQUEST };
  }
  function success() {
    return { type: accountConstants.UPDATE_PASSWORD_SUCCESS };
  }
  function failure(error) {
    return { type: accountConstants.UPDATE_PASSWORD_FAILURE, error };
  }
}

// #region getAccountDetails
const { GET_ACCOUNT_REQUEST, GET_ACCOUNT_SUCCESS, GET_ACCOUNT_FAILURE } = accountConstants;

export const getAccountDetailsRequest = createAction(GET_ACCOUNT_REQUEST);

export const getAccountDetailsSuccess = createAction<FlipdishApi.AccountDetail>(
  GET_ACCOUNT_SUCCESS
);

export const getAccountDetailsFailure = createAction<Error>(GET_ACCOUNT_FAILURE);

export function getAccountDetails(throwError = false) {
  return async (dispatch: ThunkDispatch) => {
    try {
      dispatch(getAccountDetailsRequest());

      const account = await accountService.getAccountDetails();
      dispatch(getAccountDetailsSuccess(account));

      if (account.Language) {
        dispatch(languageActions.setLanguage(account.Language));
      }

      return account;
    } catch (error) {
      dispatch(getAccountDetailsFailure(error));
      if (throwError) {
        return Promise.reject(error);
      }
    }
  };
}
// #endregion

// #region loginByPassword
const { LOGIN_REQUEST, LOGIN_SUCCESS, LOGIN_FAILURE } = accountConstants;

export type LoginByPasswordRequest = ReturnType<typeof loginByPasswordRequest>;
export const loginByPasswordRequest = (email: string) => action(LOGIN_REQUEST, email);

export type LoginByPasswordSuccess = ReturnType<typeof loginByPasswordSuccess>;
export const loginByPasswordSuccess = (email: string) => action(LOGIN_SUCCESS, email);

export type LoginByPasswordFailure = ReturnType<typeof loginByPasswordFailure>;
export const loginByPasswordFailure = (email: string, error: Error) =>
  actionError(LOGIN_FAILURE, email, error);

export function loginByPassword(email: string, password: string) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(loginByPasswordRequest(email));

      const account = await accountService.login(email, password);
      dispatch(loginByPasswordSuccess(account.email));
      await dispatch(getAccountDetails());
    } catch (error) {
      dispatch(loginByPasswordFailure(email, error));
      throw error;
    }
  };
}
// #endregion

// #region loginViaEmail
const {
  LOGIN_REQUEST_PIN_REQUEST,
  LOGIN_REQUEST_PIN_SUCCESS,
  LOGIN_REQUEST_PIN_FAILURE,
} = accountConstants;

export type LoginViaEmailRequest = ReturnType<typeof loginViaEmailRequest>;
export const loginViaEmailRequest = (email: string) => action(LOGIN_REQUEST_PIN_REQUEST, email);

export type LoginViaEmailSuccess = ReturnType<typeof loginViaEmailSuccess>;
export const loginViaEmailSuccess = (email: string, pinSent: boolean, forceOktaLogin: boolean) =>
  action(LOGIN_REQUEST_PIN_SUCCESS, { email, pinSent, forceOktaLogin });

export type LoginViaEmailFailure = ReturnType<typeof loginViaEmailFailure>;
export const loginViaEmailFailure = (email: string, error: Error) =>
  actionError(LOGIN_REQUEST_PIN_FAILURE, { email }, error);

export function loginViaEmail(email: string) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(loginViaEmailRequest(email));

      const result = await accountService.loginRequestPin(email);
      dispatch(loginViaEmailSuccess(result.email, result.pinSent, result.forceOktaLogin));
    } catch (error) {
      dispatch(loginViaEmailFailure(email, error));
      throw error;
    }
  };
}
// #endregion

function requestPin(email) {
  return (dispatch) => {
    dispatch(request());
    return accountService.loginRequestPin(email).then(
      (response) => {
        dispatch(success(response));
      },
      (error) => {
        dispatch(failure(error));
      }
    );
  };

  function request() {
    return { type: accountConstants.REQUEST_PIN_REQUEST, email };
  }

  function success(response) {
    return {
      type: accountConstants.REQUEST_PIN_SUCCESS,
      payload: response,
    };
  }

  function failure(error) {
    return { type: accountConstants.REQUEST_PIN_FAILURE, error };
  }
}

// #region requestPasswordChangePin
function requestPasswordChangePin(email) {
  return (dispatch) => {
    dispatch(request());
    return accountService.requestPasswordChangePin(email).then(
      (response) => {
        dispatch(success(response));
      },
      (error) => {
        dispatch(failure(error));
      }
    );
  };

  function request() {
    return { type: accountConstants.REQUEST_PIN_REQUEST, email };
  }

  function success(response) {
    return {
      type: accountConstants.REQUEST_PIN_SUCCESS,
      payload: response,
    };
  }

  function failure(error) {
    return { type: accountConstants.REQUEST_PIN_FAILURE, error };
  }
}
// #end region

// #region loginWithPin
const { LOGIN_WITH_PIN_REQUEST, LOGIN_WITH_PIN_SUCCESS, LOGIN_WITH_PIN_FAILURE } = accountConstants;

export type LoginWithPinRequest = ReturnType<typeof loginWithPinRequest>;
export const loginWithPinRequest = (email: string) => action(LOGIN_WITH_PIN_REQUEST, email);

export type LoginWithPinRequesSuccess = ReturnType<typeof loginWithPinSuccess>;
export const loginWithPinSuccess = (email: string) => action(LOGIN_WITH_PIN_SUCCESS, email);

export type LoginWithPinRequesFailure = ReturnType<typeof loginWithPinFailure>;
export const loginWithPinFailure = (email: string, error: Error) =>
  actionError(LOGIN_WITH_PIN_FAILURE, email, error);

export function loginWithPin(email: string, pin: string) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(loginWithPinRequest(email));

      const account = await accountService.loginWithPin(email, pin);
      dispatch(loginWithPinSuccess(account.email));
      await dispatch(getAccountDetails());
    } catch (error) {
      dispatch(loginWithPinFailure(email, error));
      throw error;
    }
  };
}
// #endregion

// #region loginWithPin
const { LOGIN_SSO_REQUEST, LOGIN_SSO_SUCCESS, LOGIN_SSO_FAILURE } = accountConstants;

export type LoginSsoRequest = ReturnType<typeof loginSsoRequest>;
export const loginSsoRequest = (email: string) => action(LOGIN_SSO_REQUEST, email);

export type LoginSsoRequesSuccess = ReturnType<typeof loginSsoSuccess>;
export const loginSsoSuccess = (email: string) => action(LOGIN_SSO_SUCCESS, email);

export type LoginSsoRequesFailure = ReturnType<typeof loginSsoFailure>;
export const loginSsoFailure = (email: string, error: Error) =>
  actionError(LOGIN_SSO_FAILURE, email, error);

export function loginSso(email: string, token?: string) {
  return async (dispatch: ThunkDispatch) => {
    try {
      dispatch(loginSsoRequest(email));
      const account = await accountService.loginSso(email, token);
      dispatch(loginSsoSuccess(account.email));
      await dispatch(getAccountDetails());
    } catch (error) {
      dispatch(notifyError({ message: 'Something_went_wrong', translate: true }));
      dispatch(loginSsoFailure(email, error));
      throw error;
    }
  };
}
// #endregion

// #region loginCreatePassword
const {
  CREATE_PASSWORD_REQUEST,
  CREATE_PASSWORD_SUCCESS,
  CREATE_PASSWORD_FAILURE,
} = accountConstants;

export type LoginCreatePasswordRequest = ReturnType<typeof loginCreatePasswordRequest>;
export const loginCreatePasswordRequest = (tokenId: string) =>
  action(CREATE_PASSWORD_REQUEST, tokenId);

export type LoginCreatePasswordSuccess = ReturnType<typeof loginCreatePasswordSuccess>;
export const loginCreatePasswordSuccess = (tokenId: string) =>
  action(CREATE_PASSWORD_SUCCESS, tokenId);

export type LoginCreatePasswordFailure = ReturnType<typeof loginCreatePasswordFailure>;
export const loginCreatePasswordFailure = (tokenId: string, error: Error) =>
  actionError(CREATE_PASSWORD_FAILURE, tokenId, error);

export function loginCreatePassword(password: string, token: string, tokenId: string) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(loginCreatePasswordRequest(tokenId));

      await accountService.createPassword(password, token, tokenId);
      dispatch(loginCreatePasswordSuccess(tokenId));
    } catch (error) {
      dispatch(loginCreatePasswordFailure(tokenId, error));
      throw error;
    }
  };
}
// #endregion

// #region create password with pin
const {
  CREATE_PASSWORD_WITH_PIN_REQUEST,
  CREATE_PASSWORD_WITH_PIN_SUCCESS,
  CREATE_PASSWORD_WITH_PIN_FAILURE,
} = accountConstants;

export type CreatePasswordWithPinRequest = ReturnType<typeof createPasswordWithPinRequest>;
export const createPasswordWithPinRequest = (props: FlipdishApi.ChangePasswordModel) =>
  action(CREATE_PASSWORD_WITH_PIN_REQUEST, props);

export type CreatePasswordWithPinSuccess = ReturnType<typeof createPasswordWithPinSuccess>;
export const createPasswordWithPinSuccess = (props: FlipdishApi.ChangePasswordModel) =>
  action(CREATE_PASSWORD_WITH_PIN_SUCCESS, props);

export type CreatePasswordWithPinFailure = ReturnType<typeof createPasswordWithPinFailure>;
export const createPasswordWithPinFailure = (
  props: FlipdishApi.ChangePasswordModel,
  error: Error
) => actionError(CREATE_PASSWORD_WITH_PIN_FAILURE, props, error);

function createPasswordWithPin(props) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(createPasswordWithPinRequest(props));
      await accountService.createPasswordWithPin(
        props.Pin,
        props.NewPassword,
        props.RecaptchaToken
      );
      dispatch(createPasswordWithPinSuccess(props));
      dispatch(notifySaved());
      dispatch(getAccountDetails());
    } catch (error) {
      dispatch(createPasswordWithPinFailure(props, error));
      if (error.body?.ErrorCode === 99) {
        dispatch(notifyError({ message: 'Invalid_code' }));
      } else if (passwordValidationApiErrors.includes(error.body?.Message)) {
        dispatch(notifyError({ message: error.body?.Message, translate: true }));
      } else {
        dispatch(notifyError({ message: 'Something_went_wrong' }));
      }
    }
  };
}
//#endregion

// #region loginPasswordReset
const { RESET_PASSWORD_REQUEST, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAILURE } = accountConstants;

export type LoginPasswordResetRequest = ReturnType<typeof loginPasswordResetRequest>;
export const loginPasswordResetRequest = (email: string) => action(RESET_PASSWORD_REQUEST, email);

export type LoginPasswordResetSuccess = ReturnType<typeof loginPasswordResetSuccess>;
export const loginPasswordResetSuccess = (email: string) => action(RESET_PASSWORD_SUCCESS, email);

export type LoginPasswordResetFailure = ReturnType<typeof loginPasswordResetFailure>;
export const loginPasswordResetFailure = (email: string, error: Error) =>
  actionError(RESET_PASSWORD_FAILURE, email, error);

export function loginPasswordReset(email: string, token?: string) {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      dispatch(loginPasswordResetRequest(email));

      await accountService.requestPasswordReset(email, token);
      dispatch(loginPasswordResetSuccess(email));
    } catch (error) {
      dispatch(loginPasswordResetFailure(email, error));
      throw error;
    }
  };
}
// #endregion

// #region logout
const { LOGOUT_SUCCESS, LOGOUT_FAILURE } = accountConstants;

// export type LogoutRequest = ReturnType<typeof logoutRequest>;
// export const logoutRequest = () =>
//   action(LOGOUT_REQUEST);

export type LogoutSuccess = ReturnType<typeof logoutSuccess>;
export const logoutSuccess = () => action(LOGOUT_SUCCESS);

export type LogoutFailure = ReturnType<typeof logoutFailure>;
export const logoutFailure = (error: Error) => actionError(LOGOUT_FAILURE, undefined, error);

export function logout() {
  return async (dispatch: ThunkDispatch, getState: () => AppState) => {
    try {
      closeConnection();
      // dispatch(logoutRequest());

      await accountService.logout();
      dispatch(logoutSuccess());

      dispatch({ type: accountConstants.RESET_ALL });
    } catch (error) {
      dispatch(logoutFailure(error));
      throw error;
    }
  };
}
// #endregion

function skipSignupStep(stepEnum: FlipdishApi.SignupStep.ActionEnum) {
  return (dispatch) => {
    dispatch(request());
    accountService.skipSignupStep(stepEnum).then(
      () => {
        dispatch(success());
        dispatch(getAccountDetails());
        googleAnalytics.sendSuccessfulSignUp();
      },
      (error) => {
        dispatch(failure(error));
      }
    );
  };

  function request() {
    return { type: accountConstants.SKIP_SIGNUP_STEP_REQUEST };
  }
  function success() {
    return { type: accountConstants.SKIP_SIGNUP_STEP_SUCCESS };
  }
  function failure(error) {
    return { type: accountConstants.SKIP_SIGNUP_STEP_FAILURE, error };
  }
}

function answerSignupQuestion(answerId: number) {
  return (dispatch) => {
    dispatch(request());
    accountService.answerSignupQuestion(answerId).then(
      () => {
        dispatch(success());
        dispatch(getAccountDetails());
      },
      (error) => {
        dispatch(failure(error));
      }
    );
  };

  function request() {
    return { type: accountConstants.ANSWER_SIGNUP_QUESTION_REQUEST };
  }
  function success() {
    return { type: accountConstants.ANSWER_SIGNUP_QUESTION_SUCCESS };
  }
  function failure(error) {
    return { type: accountConstants.ANSWER_SIGNUP_QUESTION_FAILURE, error };
  }
}

function getLocalisedTimeZones() {
  return (dispatch) => {
    dispatch(request());
    accountService.getLocalisedTimeZones().then(
      (data) => {
        dispatch(success(data));
      },
      (error) => {
        dispatch(failure());
      }
    );
  };

  function request() {
    return { type: accountConstants.GET_LOCALIZED_TIMEZONES_REQUEST };
  }
  function success(timeZones) {
    return {
      type: accountConstants.GET_LOCALIZED_TIMEZONES_SUCCESS,
      timeZones,
    };
  }
  function failure() {
    return {
      type: accountConstants.GET_LOCALIZED_TIMEZONES_FAILURE,
      timeZones: timeZoneDefinitions,
    };
  }
}

//#region GDPR

export const setGdprSelection = createAction<CookiesPreferences | undefined>('ACCOUNT_SET_GDPR');

//#endregion

export const accountActions = {
  answerSignupQuestion,
  create,
  createPassword: loginCreatePassword,
  createPasswordWithPin,
  getAccountDetails,
  login: loginByPassword,
  loginRequestPin: loginViaEmail,
  loginWithPin,
  logout,
  requestPasswordChangePin,
  requestPasswordReset: loginPasswordReset,
  setupSurvey,
  skipSignupStep,
  update,
  updatePassword,
  setTimeZoneBasedOnLatLng,
  getLocalisedTimeZones,
  requestPin,
  getSupportedCountries,
  setGdprSelection,
};
